import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { PoundSterling, Landmark, AlertOctagon, Gauge, Loader2 } from "lucide-react";
import { useAnalytics } from "@/lib/useAnalytics";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard, SectionTitle } from "@/components/ChartCard";
import { fmtGBP, fmtGBP2, monthLabel, CHART, tooltipStyle } from "@/lib/format";

const EXC_COLORS = [CHART.bad, CHART.warn, CHART.road, CHART.cfs, CHART.air];

const Finance = () => {
  const { data, isLoading } = useAnalytics("finance", "/analytics/finance");
  if (isLoading || !data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;
  const freight = data.freight_by_mode.map((d) => ({ ...d, label: monthLabel(d.month) }));
  const duty = data.duty_by_month.map((d) => ({ ...d, label: monthLabel(d.month) }));
  const exc = data.exceptional_by_month.map((d) => ({ ...d, label: monthLabel(d.month) }));
  const cpu = data.cost_per_unit_by_month.map((d) => ({ ...d, label: monthLabel(d.month) }));

  return (
    <div>
      <SectionTitle title="Finance" description="Monthly cost analysis — freight, duty, exceptional charges and cost per unit" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard testId="kpi-freight" label="Freight Cost" value={fmtGBP(t.total_freight)} icon={PoundSterling} info="Total carrier freight charges across all modes (Sea, Air, Courier) for the selected period." />
        <KpiCard testId="kpi-duty" label="Duty Cost" value={fmtGBP(t.total_duty)} icon={Landmark} info="Total import duty and taxes payable on goods for the selected period." />
        <KpiCard testId="kpi-exceptional" label="Exceptional Cost" value={fmtGBP(t.total_exceptional)} icon={AlertOctagon} tone="warn" info="Unplanned charges such as Detention & Demurrage, waiting time, storage, cancelled bookings and re-work." />
        <KpiCard testId="kpi-totalcost" label="Total Cost" value={fmtGBP(t.total_cost)} icon={PoundSterling} info="All-in logistics spend = freight + duty + exceptional charges, across all modes." />
        <KpiCard testId="kpi-cpu" label="Avg Cost / Unit" value={fmtGBP2(t.avg_cost_per_unit)} icon={Gauge} info="Average logistics cost to move one unit (piece) = Total Cost ÷ total units shipped." />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard testId="chart-freight-mode" title="Freight Cost by Mode" subtitle="Monthly, stacked by transport mode">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={freight} margin={{ left: 6, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGBP(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Sea" stackId="a" fill={CHART.sea} barSize={30} />
              <Bar dataKey="Air" stackId="a" fill={CHART.air} barSize={30} />
              <Bar dataKey="Courier" stackId="a" fill={CHART.cfs} radius={[3, 3, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-cpu" title="Total Cost per Unit by Month" subtitle="Blended cost efficiency">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cpu} margin={{ left: 6, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `£${v}`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGBP2(v)} />
              <Line type="monotone" dataKey="cpu" name="Cost / unit" stroke={CHART.road} strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-duty" title="Duty Cost by Month" subtitle="Import duty spend">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={duty} margin={{ left: 6, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGBP(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="duty" name="Duty" fill={CHART.lcl} radius={[3, 3, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-exceptional" title="Exceptional Costs by Type" subtitle="D&D, waiting time, cancellations & more">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.exceptional_by_type} layout="vertical" margin={{ left: 50, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="type" width={130} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" style={{ fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGBP(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="cost" radius={[0, 3, 3, 0]} barSize={18}>
                {data.exceptional_by_type.map((_, i) => (
                  <Cell key={i} fill={EXC_COLORS[i % EXC_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default Finance;
